import React from "react";
import AfterLoginHeader from "../Layouts/AfterLoginHeader";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  getCustomerAddressAdd,
  ResetAddAddressState,
} from "../../store/Slice/customerAddAddressSlice";
import { Toast } from "primereact/toast";
import { getLabelDetailList } from "../../store/Slice/labelDetailSlice";

const AddAddress = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const toast = React.useRef(null);

  const customerUniqueCode = sessionStorage.getItem("Customer-vUniqueCode");
  const customerIdValue = sessionStorage.getItem("CustomerId");

  const [address, setAddress] = React.useState("");
  const [city, setCity] = React.useState("");
  const [zipCode, setZipCode] = React.useState("");
  const [state, setState] = React.useState("");
  const [country, setCountry] = React.useState("");

  const [addressError, setAddressError] = React.useState("");
  const [cityError, setCityError] = React.useState("");
  const [zipCodeError, setZipCodeError] = React.useState("");
  const [stateError, setStateError] = React.useState("");
  const [countryError, setCountryError] = React.useState("");

  const [loading, setLoading] = React.useState("");

  const { customerAddressAddData } = useSelector(
    (state) => state.customerAddressAddList
  );

  const { labelDetailData } = useSelector((state) => state.labelDetailList);

  const isCustomerLoggedIn =
    customerUniqueCode && customerUniqueCode !== "undefined";

  const handleCancelClick = () => {
    navigate("/profile");
  };

  const handleAddressChange = (event) => {
    const addressValue = event.target.value;
    setAddress(addressValue);
    event.target.value.trim() !== "" && setAddressError("");
    if (addressValue.trim() === "" && addressValue.length === 0) {
      setAddressError("Address is Required.");
    } else {
      setAddressError("");
    }
  };

  const handleCityChange = (event) => {
    const cityValue = event.target.value;
    setCity(cityValue);
    event.target.value.trim() !== "" && setCityError("");
    if (cityValue.trim() === "" && cityValue.length === 0) {
      setCityError("City is Required.");
    } else {
      setCityError("");
    }
  };

  const handleZipCodeChange = (event) => {
    const zipCodeValue = event.target.value;
    setZipCode(zipCodeValue);
    event.target.value.trim() !== "" && setZipCodeError("");
    if (zipCodeValue.trim() === "" && zipCodeValue.length === 0) {
      setZipCodeError("Zip Code is Required.");
    } else {
      setZipCodeError("");
    }
  };

  const handleStateChange = (event) => {
    const stateValue = event.target.value;
    setState(stateValue);
    event.target.value.trim() !== "" && setStateError("");
    if (stateValue.trim() === "" && stateValue.length === 0) {
      setStateError("State is Required.");
    } else {
      setStateError("");
    }
  };

  const handleCountryChange = (event) => {
    const countryValue = event.target.value;
    setCountry(countryValue);
    event.target.value.trim() !== "" && setCountryError("");
    if (countryValue.trim() === "" && countryValue.length === 0) {
      setCountryError("Country is Required.");
    } else {
      setCountryError("");
    }
  };

  const handleValidAddressData = React.useCallback(() => {
    let isValid = true;

    setAddressError();
    setCityError();
    setZipCodeError();
    setStateError();
    setCountryError();

    if (address.trim() === "" && address.length === 0) {
      setAddressError("Address is Required.");
      isValid = false;
    }
    if (city.trim() === "" && city.length === 0) {
      setCityError("City is Required.");
      isValid = false;
    }
    if (zipCode.trim() === "" && zipCode.length === 0) {
      setZipCodeError("Zip Code is Required.");
      isValid = false;
    }
    if (state.trim() === "" && state.length === 0) {
      setStateError("State is Required.");
      isValid = false;
    }
    if (country.trim() === "" && country.length === 0) {
      setCountryError("Country is Required.");
      isValid = false;
    }

    return isValid;
  }, [address, city, zipCode, state, country]);

  const handleAddAddress = React.useCallback(
    async (e) => {
      e.preventDefault();

      const isValidData = handleValidAddressData();
      if (isValidData) {
        if (isCustomerLoggedIn) {
          dispatch(
            getCustomerAddressAdd({
              iCustomerId: +customerIdValue,
              tAddress: address,
              vCity: city,
              vZipCode: zipCode,
              vState: state,
              vCountry: country,
            })
          );
          setLoading(true);
        }
      }
    },
    [
      dispatch,
      handleValidAddressData,
      isCustomerLoggedIn,
      customerIdValue,
      address,
      city,
      state,
      country,
      zipCode,
    ]
  );

  React.useEffect(() => {
    if (!customerAddressAddData) return;

    const { code, message } = customerAddressAddData;

    if (code === 201) {
      toast.current.show({
        severity: "success",
        summary: message || "Address added successfully.",
        life: 2000,
        className: "custom-toast",
      });

      setTimeout(() => {
        dispatch(ResetAddAddressState());
        setLoading(false);
        setAddress("");
        setCity("");
        setZipCode("");
        setState("");
        setCountry("");
        navigate("/profile");
      }, 2000);
    } else if (code === 400 || code === 404) {
      toast.current.show({
        severity: "error",
        summary: message || "Failed to add address.",
        life: 2000,
        className: "custom-toast-error",
      });
      setLoading(false);
    }
  }, [dispatch, navigate, customerAddressAddData]);

  const path = location.pathname.split("/")[1];

  React.useEffect(() => {
    dispatch(getLabelDetailList({ vSlug: path }));
  }, [dispatch, path]);

  const getLabels = React.useMemo(() => {
    if (labelDetailData?.code !== 200) return {};
    return labelDetailData.data.reduce((acc, item) => {
      acc[item.vLabel] = item.tText;
      return acc;
    }, {});
  }, [labelDetailData]);

  const contactInformationHeading =
    getLabels["LBL_ADD_ADDRESS_CONTACT_INFO_HEADING"];
  const addAddressHeading = getLabels["LBL_ADD_ADDRESS_MAIN_HEADING"];
  const addressLabel = getLabels["LBL_ADD_ADDRESS_ADDRESS_HEADING"];
  const cityLabel = getLabels["LBL_ADD_ADDRESS_CITY_HEADING"];
  const zipCodeLabel = getLabels["LBL_ADD_ADDRESS_ZIP_CODE_HEADING"];

  const stateLabel = getLabels["LBL_ADD_ADDRESS_STATE_HEADING"];
  const countryLabel = getLabels["LBL_ADD_ADDRESS_COUNTRY_HEADING"];

  return (
    <div className="dashboard">
      <Toast ref={toast} />
      <div className="dashboard-app">
        <AfterLoginHeader />

        <div className="dashboard-content">
          <div className="session-big-section">
            <div className="row">
              <div className="col-lg-12">
                <h2 className="dash-maintitle">{addAddressHeading}</h2>

                <div className="main-card profile-maincard">
                  <div className="dashprofile-card">
                    <div className="row">
                      <div className="col-xxl-10">
                        <div className="row justify-content-center">
                          <div className="col-xxl-10 col-xl-10">
                            <h3 className="dashsmall-bluetitle">
                              {contactInformationHeading}
                            </h3>
                            <div className="login-form pt-0 pb-0">
                              <div className="row">
                                <div className="col-lg-6 col-md-6">
                                  <div className="login-inputDiv">
                                    <label
                                      htmlFor="exampleInputaddress"
                                      className="form-label"
                                    >
                                      {addressLabel}
                                    </label>
                                    <input
                                      type="text"
                                      className="form-control"
                                      id="exampleInputaddress"
                                      placeholder={`Enter Your ${
                                        addressLabel || ""
                                      }`}
                                      value={address}
                                      onChange={handleAddressChange}
                                    />
                                    {addressError && (
                                      <div className="validation-error">
                                        {addressError}
                                      </div>
                                    )}
                                  </div>
                                </div>

                                <div className="col-lg-6 col-md-6">
                                  <div className="login-inputDiv">
                                    <label
                                      htmlFor="exampleInputcity"
                                      className="form-label"
                                    >
                                      {cityLabel}
                                    </label>
                                    <input
                                      type="city"
                                      className="form-control"
                                      id="exampleInputcity"
                                      placeholder={`Enter Your ${
                                        cityLabel || ""
                                      }`}
                                      value={city}
                                      onChange={handleCityChange}
                                    />
                                    {cityError && (
                                      <div className="validation-error">
                                        {cityError}
                                      </div>
                                    )}
                                  </div>
                                </div>
                                <div className="col-lg-6 col-md-6">
                                  <div className="login-inputDiv">
                                    <label
                                      htmlFor="exampleInputzipcode"
                                      className="form-label"
                                    >
                                      {zipCodeLabel}
                                    </label>
                                    <input
                                      type="text"
                                      className="form-control"
                                      id="exampleInputzip"
                                      placeholder={`Enter Your ${
                                        zipCodeLabel || ""
                                      }`}
                                      value={zipCode}
                                      onChange={handleZipCodeChange}
                                    />
                                    {zipCodeError && (
                                      <div className="validation-error">
                                        {zipCodeError}
                                      </div>
                                    )}
                                  </div>
                                </div>
                                <div className="col-lg-6 col-md-6">
                                  <div className="login-inputDiv">
                                    <label
                                      htmlFor="exampleInputcity"
                                      className="form-label"
                                    >
                                      {stateLabel}
                                    </label>
                                    <input
                                      type="state"
                                      className="form-control"
                                      id="exampleInputstate"
                                      placeholder={`Enter Your ${
                                        stateLabel || ""
                                      }`}
                                      value={state}
                                      onChange={handleStateChange}
                                    />
                                    {stateError && (
                                      <div className="validation-error">
                                        {stateError}
                                      </div>
                                    )}
                                  </div>
                                </div>

                                <div className="col-lg-6 col-md-6">
                                  <div className="login-inputDiv">
                                    <label
                                      htmlFor="exampleInputcountry"
                                      className="form-label"
                                    >
                                      {countryLabel}
                                    </label>
                                    <input
                                      type="text"
                                      className="form-control"
                                      id="exampleInputcountry"
                                      placeholder={`Enter Your ${
                                        countryLabel || ""
                                      }`}
                                      value={country}
                                      onChange={handleCountryChange}
                                    />
                                    {countryError && (
                                      <div className="validation-error">
                                        {countryError}
                                      </div>
                                    )}
                                  </div>
                                </div>
                                <div className="col-lg-12">
                                  <div className="d-flex gap-3">
                                    {loading ? (
                                      <>
                                        {" "}
                                        <button
                                          className="success-orangebtn"
                                          type="button"
                                          disabled
                                        >
                                          <span
                                            className="spinner-border spinner-border-sm me-1 custom-spinner"
                                            role="status"
                                            aria-hidden="true"
                                          ></span>
                                          Loading...
                                        </button>
                                      </>
                                    ) : (
                                      <button
                                        type="submit"
                                        className="success-orangebtn"
                                        onClick={handleAddAddress}
                                      >
                                        Submit
                                      </button>
                                    )}
                                    <button
                                      className="success-orangebtn success-grayborderbtn"
                                      onClick={handleCancelClick}
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddAddress;
